/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {areEqual} from 'react-window';
import Box from '@material-ui/core/Box';
import {makeStyles} from '@material-ui/core/styles';
import axios from 'axios';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {GlobalSearchResult, transformSearch} from '@ion/core/src/services/GlobalSearch';
import {SearchType} from '@ion/core/src/types/services';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import {DLSTheme} from '@ion/dls/src/types/types';
import useRootManager from '../../client/useRootManager';
import {TertiaryButton} from '../../components/buttons/TertiaryButton';
import TextField from '../../components/textfield/TextField';
import {useIsPhone} from '../../hooks/useMedia';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import {TopNavSearchIcon} from '../../icons/TopNavSearchIcon';
import {UserNotificationDialog} from '../../notifications/UserNotificationDialog';
import {dlsAlt, dlsColor, dlsLayout, dlsTypography} from '../../theme/theme';
import {HeaderTypography} from '../../typography/HeaderTypography';
import {LabelTypography} from '../../typography/LabelTypography';
import SearchMapLocationPopup from '../components/SearchMapLocationPopup';
import {mapViewGoToOptions} from '../constants';
import {textFieldStyles} from '../geofences/SearchAddress';
import {
  createSearchPoint,
  getLocationDetails,
  getLocationlabel,
  getLocations
} from '../helpers/MapViewUtils';
import {FindNearbyType} from '../layers/AssetLayer';
import {SearchPlaceItem} from './SearchPlaceItem';

// defines the prop definitions for the LayerChooser
export type SearchViewProps = {
  mapView: __esri.MapView;
  isVisible: boolean;
  isOpeningFromBottomSheet?: boolean;
  useExtent?: boolean;
  savedSearchHistoryItems?: SavedPlacesProps[];
  saveSearchHistory?: Function;
  searchResult?: GlobalSearchResult | null;
  MapControlsBreadCrumbs?: null | ReactElement;
  onLocationPopupFindNearby?: Function;
  isFindNearbyPopup?: boolean;
  className?: string;
  onClose?: Function;
  userPreferenceName?: string;
  onFindNearyBy?: Function;
  onFindNearbyBreadcrumbBackClick?: Function;
  onFindNearbyPopupStatusChange?: Function;
  onFindButtonStateChange?: Function;
  findLabel?: FindNearbyType;
  onCloseSearchPopup?: Function;
  isAutoRefresh?: boolean;
  setSelectedTrackingSearch?: Function;
  searchedResultRef?: any;
  useHereApi?: boolean;
  moveAllToolsToTop?: boolean;
  isTrackingV2?: boolean;
  setFindNearbyActions?: Function;
};

export type SavedPlacesProps = {
  longitude: number;
  latitude: number;
  magicKey?: string;
  locationId?: string;
  text: string;
  label?: string;
  title?: string;
  id?: string;
  position?: {
    lng: number;
    lat: number;
  };
};

export type PlaceItemProps = {
  result: SavedPlacesProps;
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
  onDeleteItem?: Function;
  isSavedPlace?: boolean;
  placeSearchText?: string;
  isSearchHistoryPlace?: boolean;
};

type StyleProps = {
  moveAllToolsToTop?: boolean;
};

/**
 * Defines the common zoom level used by map search components.
 */
export const SEARCH_ZOOM_LEVEL = 11;

/**
 * Provides a utility for generating an ESRI Search Suggestion request extent
 * parameters.
 */
export const getSearchExtentParams = (
  view: __esri.MapView,
  useExtent = false
): string | undefined => {
  const {xmin, ymin, xmax, ymax} = view.extent;
  return useExtent
    ? JSON.stringify({
      xmin,
      ymin,
      xmax,
      ymax,
      spatialReference : {
        wkid : view.extent?.spatialReference?.wkid
      }
    })
    : undefined;
};

const toggleStyle = (style1: string, style2: string) =>
  ({moveAllToolsToTop}: StyleProps) => moveAllToolsToTop ? style1 : style2;

// defines the styles used by the LayerChooser
export const useStyles = makeStyles<DLSTheme, StyleProps>(theme => ({
  mapView : {
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    boxShadow       : dlsAlt('components', 'boxShadow', 'shadow4'),
    position        : 'absolute',
    top             : 109,
    minWidth        : 285,
    right           : toggleStyle('135px', '50px')
  },
  searchHeaderContainer : {
    position     : 'absolute',
    width        : '285px',
    height       : '26px',
    background   : dlsColor('grey10'),
    borderRadius : '4px 4px 0px 0px'
  },
  searchHeader : {
    position      : 'absolute',
    width         : '81px',
    height        : '16px',
    left          : '15px',
    top           : '5px',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontWeight    : 700,
    fontSize      : '12px !important',
    lineHeight    : '16px !important',
    letterSpacing : '0.5px !important',
    color         : dlsColor('white')
  },
  searchIcon : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  searchMapunderline : {
    '&:after' : {
      borderColor : dlsColor('grey3')
    },
    '&:before' : {
      borderColor       : `${dlsColor('grey3')} !important`,
      borderBottomWidth : '1px !important'
    }
  },
  searchMapFieldLabel : {
    color         : dlsColor('grey3'),
    marginBottom  : '8px',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontWeight    : 400,
    fontSize      : '14px !important',
    lineHeight    : '20px !important',
    letterSpacing : '0.5px !important',
    ...dlsTypography('body2')
  },
  mapViewMobile : {
    padding : '30px 8px 0px 8px'
  },
  searchListBox : {
    marginTop   : 26,
    marginLeft  : 14,
    marginRight : 14
  },
  searchList : {
    marginTop : dlsLayout('margins')
  },
  searchBreadCrumbs : {
    '& *' : {
      textTransform : 'uppercase'
    }
  },
  closeIcon : {
    cursor : 'pointer'
  },
  label1 : {
    marginLeft : 14
  },
  ...textFieldStyles
}));

// Unique identifier for the boundary layer.
export const SEARCH_LAYER_ID = 'searchPointLayer';

function SearchView({
  mapView,
  isVisible,
  useExtent,
  saveSearchHistory,
  searchResult,
  MapControlsBreadCrumbs,
  onLocationPopupFindNearby,
  isFindNearbyPopup,
  className,
  onClose,
  onFindNearyBy,
  onFindNearbyPopupStatusChange,
  onFindNearbyBreadcrumbBackClick,
  onFindButtonStateChange,
  findLabel,
  onCloseSearchPopup,
  setSelectedTrackingSearch,
  isAutoRefresh,
  searchedResultRef,
  setFindNearbyActions,
  useHereApi = false,
  isOpeningFromBottomSheet = false,
  moveAllToolsToTop = false,
  savedSearchHistoryItems = [],
  userPreferenceName = 'tracking.map.bookmarks',
  isTrackingV2 = false
}: SearchViewProps) {
  const classes                                     = useStyles({moveAllToolsToTop});
  const isPhone                                     = useIsPhone();
  const [results, setResults]                       = useState(null);
  const [isShowSaveButton, setIsShowSaveButton]     = useState(false);
  const [savedPlaces, setSavedPlaces]               = useState<SavedPlacesProps[]>(null);
  const [isMobileFullHeight, setIsMobileFullHeight] = useState(false);
  const [placeSearchText, setPlaceSearchText]       = useState('');
  const {render, createContainer}                   = useRootManager();
  const [
    isDeleteSavedLocationDialogOpen,
    setIsDeleteSavedLocationDialogOpen
  ] = useState(false);
  const currentSelectedPlace    = useRef<SavedPlacesProps | null>(null);
  const sourceCancelToken       = useRef(null);
  const savedPlacesRef          = useRef(null);
  const deletePlaceItem         = useRef(null);
  const locationPopupEl         = useRef(null);
  const inputRef                = useRef(null);
  const isSavedPlaceRef         = useRef(null);
  const isAllowedToSaveRef      = useRef(null);
  const searchViewClickRef      = useRef(null);
  const isTrackingV2Ref         = useRef(isTrackingV2);
  const setFindNearbyActionsRef = useRef(setFindNearbyActions);

  const setSearchResultRef = useCallback(value => {
    if (searchedResultRef) {
      searchedResultRef.current = value;
    }
  }, [searchedResultRef]);

  useEffect(() => {
    isTrackingV2Ref.current = isTrackingV2;
  }, [isTrackingV2]);

  useEffect(() => {
    setFindNearbyActionsRef.current = setFindNearbyActions;
  }, [setFindNearbyActions]);

  useEffect(() => {
    getSavedPlaces();
  }, []);

  useEffect(() => {
    if ((!savedPlacesRef.current || savedPlacesRef.current?.length <= 0) && !placeSearchText) {
      setSavedPlaces(savedSearchHistoryItems);
    }
  }, [savedSearchHistoryItems]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => {
      clearTimeout(timeout);
      mapView.popup.close();
    };
  }, [isVisible]);

  useEffect(() => {
    if (!searchResult) {
      mapView.popup.close();
      mapView.graphics.removeAll();
      currentSelectedPlace.current = null;
      isAllowedToSaveRef.current = false;
      isSavedPlaceRef.current = false;
      setSearchResultRef(null);
    }
    if (searchResult?.isPlace) {
      callAddress({
        selectedResult  : searchResult.item,
        isAllowedToSave : isAllowedToSaveRef.current,
        isSavedPlace    : isSavedPlaceRef.current
      });
    }
  }, [searchResult]);

  useEffect(() => {
    const {current} = currentSelectedPlace;
    if (current) {
      setGraphic(
        current?.latitude,
        current?.longitude,
        current
      );
    }
  }, [isAutoRefresh]);

  async function searchLocation(val) {
    try {
      const searchText = val?.trim() || '';
      const matchedSavePlaces = savedPlacesRef?.current?.filter(
        (place) =>
          !place?.text?.toLowerCase().indexOf(searchText?.toLowerCase())
      );
      setSavedPlaces(matchedSavePlaces);
      if (searchText) {
        const cancelToken = axios.CancelToken;
        if (typeof sourceCancelToken.current !== typeof undefined) {
          sourceCancelToken.current?.cancel();
        }
        sourceCancelToken.current = cancelToken.source();
        const suggestions = await getLocations({
          cancelToken : sourceCancelToken.current.token,
          searchText,
          useHereApi,
          mapView,
          useExtent
        });
        setResults(suggestions);
      } else {
        setResults(null);
        if (typeof sourceCancelToken.current !== typeof undefined) {
          sourceCancelToken.current?.cancel();
        }
        if (!savedPlacesRef.current || savedPlacesRef.current?.length <= 0) {
          setSavedPlaces(savedSearchHistoryItems);
        }
      }
    } catch (err) {
      if (!axios.isCancel(err.error)) {
        error(err);
      }
    }
  }

  const searchOnChange = useCallback(debounce(value => {
    setIsMobileFullHeight(true);
    setIsShowSaveButton(false);
    searchLocation(value);
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    if (!value) {
      setResults(null);
    }
  }, 200), []);

  function setPopup(
    selectedLocation: SavedPlacesProps,
    isSavedPlace = false,
    isAllowedToSave = true
  ) {
    try {
      if (selectedLocation) {
        const {
          magicKey,
          longitude,
          latitude,
          label,
          text
        } = selectedLocation;
        const container = createContainer('search-view-popup-container');
        render(
          <SearchMapLocationPopup place={label}
            isTrackingV2={isTrackingV2Ref.current}
            ref={locationPopupEl}
            address={text}
            mapView={mapView}
            isFindNearbyPopup={isFindNearbyPopup}
            onFindNearbyBreadcrumbBackClick={onFindNearbyBreadcrumbBackClick}
            selectedPlace={selectedLocation}
            findLabel={findLabel}
            onFindNearyBy={onFindNearyBy}
            onFindButtonStateChange={onFindButtonStateChange}
            onFindNearbyPopupStatusChange={onFindNearbyPopupStatusChange}
            isSavedPlace={isSavedPlace}
            onCloseSearchPopup={onCloseSearchPopup}
            savePlace={savePlace}
            isAllowedToSave={isAllowedToSave}
            openDeleteSavedLocationDialog={openDeleteSavedLocationDialog}
            onLocationPopupFindNearby={onLocationPopupFindNearby}
            setFindNearbyActions={setFindNearbyActionsRef.current} />,
          container
        );
        mapView
          .goTo({
            center : [longitude, latitude],
            zoom   : SEARCH_ZOOM_LEVEL
          }, mapViewGoToOptions)
          .then(function() {
            mapView.popup.open({
              location : {
                type : 'point',
                longitude,
                latitude
              },
              content : container
            });
          });
      }
    } catch (err) {
      error('error', err);
    }
  };

  const onSelected = useCallback(function({results}) {
    results.forEach(function(result) {
      const selected = currentSelectedPlace.current;
      if (selected) {
        const {magicKey, locationId} = result.graphic?.attributes;
        const {magicKey: selectedMagicKey, locationId: selectedLocationId} = selected;
        if ((!isNil(magicKey) || !isNil(locationId)) && (
          magicKey === selectedMagicKey ||
            locationId === selectedLocationId
        )) {
          if (isFindNearbyPopup) {
            onFindNearbyPopupStatusChange(false);
          } else {
            setPopup(selected, isSavedPlaceRef.current, isAllowedToSaveRef.current);
          }
        }
      }
    });
  }, [currentSelectedPlace, isFindNearbyPopup, setPopup]);

  const searchViewClickHandler = useCallback(function(event) {
    mapView.hitTest(event).then(onSelected);
  }, [onSelected]);

  useEffect(() => {
    searchViewClickRef.current = mapView.on('click', searchViewClickHandler);
    return () => {
      searchViewClickRef.current?.remove?.('click', searchViewClickHandler);
    };
  }, []);

  const setGraphic = async(latitude: number, longitude: number, selectedLocation: SavedPlacesProps) => {
    const graphic = await createSearchPoint(latitude, longitude, selectedLocation);
    mapView.graphics.add(graphic);
    setSearchResultRef(graphic);
  };

  async function callAddress({
    selectedResult,
    isSavedPlace = false,
    isAllowedToSave = true
  }) {
    try {
      mapView.graphics.removeAll();
      const selectedLocation = await getLocationDetails(selectedResult) as unknown as SavedPlacesProps;
      if (!selectedLocation) {
        return true;
      }
      const {latitude, longitude} = selectedLocation;
      setGraphic(latitude, longitude, selectedLocation);
      setPopup(selectedLocation, isSavedPlace, isAllowedToSave);
      currentSelectedPlace.current = selectedLocation;
      saveSearchHistory?.(selectedLocation);
    } catch (err) {
      error(err);
    }
  }

  const getSavedPlaces = useCallback(() => {
    const savedPlaces      = UserPreferences.get(userPreferenceName);
    savedPlacesRef.current = savedPlaces;
    setSavedPlaces(savedPlaces);
  }, [userPreferenceName]);

  const savePlace = useCallback(() => {
    isSavedPlaceRef.current = true;
    setIsShowSaveButton(false);
    if (currentSelectedPlace.current) {
      const {
        longitude,
        latitude,
        magicKey,
        locationId,
        label,
        text
      } = currentSelectedPlace.current;

      const newPlaces = [{
        label : text,
        longitude,
        latitude,
        magicKey,
        locationId,
        text
      }, ...(savedPlacesRef.current || [])];
      const newPlacesWithoutDuplicate = removeDuplicate(
        'locationId',
        removeDuplicate('magicKey', newPlaces, false),
        false
      );
      savedPlacesRef.current = newPlacesWithoutDuplicate;
      setSavedPlaces(newPlacesWithoutDuplicate);
      locationPopupEl.current.savePlaceToPopup();
      UserPreferences.set(
        userPreferenceName,
        newPlacesWithoutDuplicate,
        true
      );
    }
  }, [userPreferenceName]);

  const onDeleteItem = useCallback(result => {
    const newSavedPlace = savedPlaces?.filter(
      ({magicKey, locationId}) => result.magicKey !== magicKey || result.locationId !== locationId
    );
    onCloseSearchPopup?.();
    savedPlacesRef.current = newSavedPlace;
    setSavedPlaces(newSavedPlace);
    setIsDeleteSavedLocationDialogOpen(false);
    mapView.popup.close();
    setPlaceSearchText('');
    UserPreferences.set(userPreferenceName, newSavedPlace, true);
    deletePlaceItem.current = null;
    currentSelectedPlace.current = null;
    setSearchResultRef(null);
  }, [savedPlaces]);

  const closeDeleteSavedLocationDialog = useCallback(() => {
    setIsDeleteSavedLocationDialogOpen(false);
  }, []);

  const openDeleteSavedLocationDialog = useCallback(place => {
    setIsDeleteSavedLocationDialogOpen(true);
    deletePlaceItem.current = place;
  }, []);

  return (
    <>
      <div className={classnames(className,
        isOpeningFromBottomSheet ? classes.mapViewMobile : classes.mapView)
      } style={{
        display : !isVisible ? 'none' : 'inherit',
        height  :
            isMobileFullHeight && isOpeningFromBottomSheet
              ? 'calc(100vh - 220px)'
              : 'auto',
        paddingBottom :
            isMobileFullHeight && isOpeningFromBottomSheet ? 0 : 32
      }}>
        {MapControlsBreadCrumbs && <MapControlsBreadCrumbs crumb={translate('maps:searchMap.header')} isPlainCrumb />}
        {!isPhone && (
          <div className={classes.searchHeaderContainer}>
            <HeaderTypography variant='h4' className={classes.searchHeader}>
              {translate('maps:searchMap.header')}
            </HeaderTypography>
          </div>
        )}
        <div className={classes.searchListBox}>
          <TextField placeholder={translate('maps:searchMap.placeholder')}
            style={{width: '100%'}}
            value={placeSearchText}
            inputRef={inputRef}
            InputLabelProps = {{
              classes : {
                root : classes.searchMapFieldLabel
              }
            }}
            InputProps={{
              classes : {
                underline : classes.searchMapunderline,
                input     : classes.noPadLeft
              },
              startAdornment : (<div className={classes.searchIcon}>
                <TopNavSearchIcon />
              </div>),
              endAdornment : (
                <>
                  {isShowSaveButton && (
                    <TertiaryButton noUnderline
                      onClick={function() {
                        savePlace();
                      }}
                      style={{
                        padding : 0
                      }}>
                      {translate('maps:searchMap.save')}
                    </TertiaryButton>
                  )}
                  {!isShowSaveButton && placeSearchText && (
                    <Box display='flex' className={classes.closeIcon}
                      onClick={function() {
                        setPlaceSearchText('');
                        searchOnChange('');
                      }}>
                      <MaterialExitCloseIcon />
                    </Box>
                  )}
                </>
              )
            }}
            onFocus={function() {
              searchLocation('');
            }}
            onChange={function({target: {value}}) {
              setPlaceSearchText(value);
              searchOnChange(value);
            }} />
        </div>
        {(!!results?.length || !!savedPlaces?.length) && (
          <div className={classes.searchList}
            style={{
              overflow  : 'auto',
              maxHeight : isOpeningFromBottomSheet
                ? 'calc(100vh - 200px)'
                : mapView.height - 200
            }}>
            {!!savedPlaces?.length && (
              <>
                {!placeSearchText && savedPlacesRef.current?.length > 0 && (
                  <LabelTypography variantType='label1' className={classes.label1}>
                    {translate('maps:searchMap.saved')}
                  </LabelTypography>
                )}
                {savedPlaces?.map((result) => {
                  const {magicKey, locationId} = result;
                  return (
                    <SearchPlaceItem key={magicKey || locationId}
                      placeSearchText={placeSearchText}
                      result={result}
                      isSavedPlace={
                        savedPlacesRef.current &&
                      savedPlacesRef.current?.length > 0
                      }
                      isSearchHistoryPlace={
                        savedPlacesRef.current &&
                      savedPlacesRef.current?.length <= 0
                      }
                      onDeleteItem={openDeleteSavedLocationDialog}
                      onClick={function() {
                        const transformLabel       = [useHereApi ? 'label' : 'text'];
                        const searchText           = getLocationlabel(result.text, useHereApi);
                        const [transformedResult]  = transformSearch([result], SearchType.place, transformLabel);
                        isAllowedToSaveRef.current = true;
                        if (savedPlacesRef.current &&
                        savedPlacesRef.current?.length > 0) {
                          setIsShowSaveButton(false);
                          isSavedPlaceRef.current = true;
                        } else {
                          isSavedPlaceRef.current = false;
                          setIsShowSaveButton(true);
                        }
                        callAddress({
                          selectedResult : result,
                          isSavedPlace   : isSavedPlaceRef.current
                        });
                        setSelectedTrackingSearch?.(transformedResult);
                        setPlaceSearchText(searchText);
                        setResults(null);
                        setIsMobileFullHeight(false);
                        onClose?.();
                      }} />
                  );
                })}
              </>
            )}

            {results?.map((result) => {
              const {magicKey, locationId, text} = result;
              const searchText = getLocationlabel(text, useHereApi);
              return (
                <SearchPlaceItem key={magicKey || locationId}
                  placeSearchText={placeSearchText}
                  result={result}
                  onClick={function() {
                    setIsShowSaveButton(true);
                    const selectedResult = result;
                    isAllowedToSaveRef.current = true;
                    isSavedPlaceRef.current = false;
                    callAddress({
                      isAllowedToSave : isAllowedToSaveRef.current,
                      isSavedPlace    : isSavedPlaceRef.current,
                      selectedResult
                    });
                    setSelectedTrackingSearch?.(result);
                    setPlaceSearchText(searchText);
                    setResults(null);
                    setSavedPlaces(null);
                    setIsMobileFullHeight(false);
                    onClose?.();
                  }} />
              );
            })}
          </div>
        )}
      </div>

      <UserNotificationDialog show={isDeleteSavedLocationDialogOpen}
        title={translate('maps:searchMap.removeTitle').toUpperCase()}
        message={translate('maps:searchMap.removeMessage')}
        actionLabel={translate('maps:searchMap.removeYes')}
        dismissLabel={translate('maps:searchMap.removeNever')}
        onAction={function() {
          onDeleteItem(deletePlaceItem.current);
          mapView.graphics.removeAll();
        }}
        onDismiss={closeDeleteSavedLocationDialog} />
    </>
  );
}

export default memo(SearchView, areEqual);
